package org.multiplatform.kotfolio.pages.compose

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ol
import org.jetbrains.compose.web.dom.Ul

@Page("/compose/navigation")
@Composable
fun ComposeNavigationPage() {
    BlogLayout(
        PageProperties(
            "Navigation 3",
            "Navigation 3 is a new library for Compose, provides full back stack control, adaptive layouts, NavDisplay and implementing NavKey for screens."
        ),
        BlogProperties("Jetpack Compose", "Navigation 3")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Navigation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Composable
                    internal fun App(
                        navController: NavHostController = rememberNavController()
                    ) = AppTheme {

                    }

                    @Composable
                    internal fun AppTheme(
                        content: @Composable () -> Unit
                    ) {
                        val scheme = rememberDynamicColorScheme(
                            seedColor = Color(0xFF5FBA9A),
                            isDark = true,
                            isAmoled = false,
                            style = PaletteStyle.Monochrome
                        )
                        MaterialTheme(
                            colorScheme = scheme,
                            content = { Surface(content = content) }
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Serializable
                    sealed class NavItem {
                        open class Item<T>(val path: T, val title: String, val icon: ImageVector) : NavItem()

                        object HomeScreen : Item<Home>(
                            path = Home,
                            title = "Home",
                            icon = Icons.Default.Home
                        )

                        object SettingsScreen : Item<Settings>(
                            path = Settings,
                            title = "Settings",
                            icon = Icons.Default.Settings
                        )
                    }

                    @Serializable
                    object Home

                    @Serializable
                    object Settings

                    //App.kt
                    @Composable
                    fun App() {
                        val navController: NavHostController = rememberNavController()
                        val navBackStackEntry by navController.currentBackStackEntryAsState()
                        var selectedItem by rememberSaveable { mutableIntStateOf(0) }

                        //BackArrow Visible
                        val bs = navController.currentBackStack.collectAsStateWithLifecycle()
                        val isBackVisible = bs.value.size > 2

                        LaunchedEffect(navBackStackEntry?.destination?.route) {
                            when (navBackStackEntry?.destination?.id) {
                                Home.serializer().generateHashCode() -> {
                                    selectedItem = 0
                                }
                                Settings.serializer().generateHashCode() -> {
                                    selectedItem = 1
                                }
                            }
                        }

                        UTXOTheme() {
                            Scaffold(
                                modifier = Modifier.fillMaxSize(),
                                bottomBar = {
                                    BottomAppBar(
                                        actions = {
                                            val navItems = listOf(NavItem.HomeScreen, NavItem.SettingsScreen)

                                            NavigationBar(
                                                tonalElevation = 0.dp
                                            ) {
                                                navItems.forEachIndexed { index, item ->
                                                    NavigationBarItem(
                                                        alwaysShowLabel = false,
                                                        icon = {
                                                            Icon(
                                                                imageVector = item.icon,
                                                                contentDescription = item.title
                                                            )
                                                        },
                                                        label = { if (selectedItem == index) Text(item.title) },
                                                        selected = selectedItem == index,
                                                        onClick = {
                                                            selectedItem = index
                                                            navController.navigate(item.path) {
                                                                popUpTo(navController.graph.startDestinationId) {
                                                                    saveState = true
                                                                }
                                                                launchSingleTop = true
                                                                restoreState = true
                                                            }
                                                        }
                                                    )
                                                }
                                            }
                                        }
                                    )
                                }
                            ) { innerPadding ->
                                NavHost(
                                    navController = navController,
                                    startDestination = Home,
                                    modifier = Modifier
                                        .fillMaxSize()
                                        .padding(innerPadding)
                                ) {
                                    animatedComposable<Home> {

                                    }
                                    animatedComposable<model.Settings> {

                                    }
                                }
                            }
                        }
                    }

                    fun <T : Any> NavHostController.navigatePoppingUpToStartDestination(route: T) {
                        navigate(route) {
                            popUpTo(graph.findStartDestination().navigatorName) {
                                saveState = true
                            }
                            launchSingleTop = true
                            restoreState = true
                        }
                    }

                    @OptIn(ExperimentalAnimationApi::class)
                    inline fun <reified T : Any> NavGraphBuilder.animatedComposable(
                        noinline content: @Composable AnimatedContentScope.(NavBackStackEntry) -> Unit
                    ) {
                        composable<T>(
                            enterTransition = { expandFromCenter() },
                            exitTransition = { shrinkToCenter() },
                            content = content
                        )
                    }

                    @ExperimentalAnimationApi
                    fun expandFromCenter(): EnterTransition {
                        return scaleIn(
                            animationSpec = tween(300),
                            initialScale = 0.8f,
                            transformOrigin = TransformOrigin.Center
                        ) + fadeIn(animationSpec = tween(300))
                    }

                    @ExperimentalAnimationApi
                    fun shrinkToCenter(): ExitTransition {
                        return scaleOut(
                            animationSpec = tween(300),
                            targetScale = 0.8f,
                            transformOrigin = TransformOrigin.Center
                        ) + fadeOut(animationSpec = tween(300))
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Navigation Argument",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                     //Navigation Controller Define
                     @Composable
                     fun rememberNavController(startDestination: String): NavController = remember {
                         NavController(startDestination)
                     }
    
                     // Create navigation controller with Login as start destination
                     val navController = rememberNavController(
                         startDestination = Screen.Login.route
                     )
                    
                     //1 Sting Type
                     composable("addGrocery/{id}") { backStackEntry ->
                        val id = backStackEntry.arguments?.getString("id")?.toIntOrNull()
                     }
                     
                     @OptIn(ExperimentalMaterial3Api::class)
                     @Composable
                     fun GroceryAddingScreen(
                         groceryId: Int?
                     ) { }
                     
                     navController.navigate("addGrocery/$groceryId")
                     
                     //2 StringType
                     composable("detail/{task}", arguments = listOf(navArgument("task") { type = NavType.StringType })) 
                     {
                        val json = it.arguments?.getString("task")!!
                        val task = Json.decodeFromString<Task>(json)
                     }
                     
                     val taskInfo = Json.encodeToString(task)
                     navController.navigate("detail/$taskInfo")
                     
                     @Serializable
                     @Entity
                     data class Task(
                         @PrimaryKey(autoGenerate = true) val id: Int = 0,
                         val title: String,
                         val isCompleted: Boolean = false
                     )
                     
                     //LongType
                     composable(
                        route = "detail/{taskId}",
                        arguments = listOf(navArgument("taskId") { type = NavType.LongType })
                    ) {
                            val taskId = it.arguments?.getLong("taskId") ?: -1L
                      }
                      
                      
                    """.trimIndent()
            )

            SpanText(
                text = "Safe Navigation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @Serializable
                    object ScreenA

                    // If we want to pass our arguments
                    @Serializable
                    data class ScreenB(
                      val name: String?,
                      val age: Int
                    )

                    //Pass argument
                    navController.navigate(ScreenB(name = null, age = 25))

                    //Get argument
                    composable<ScreenB> (
                        val args = it.toRoute<ScreenB>()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import androidx.navigation.NavController
                    import androidx.navigation.NavOptionsBuilder

                    fun <T: Any>NavController.safeNavigate(route: T, navOptionsBuilder: NavOptionsBuilder.() -> Unit) {
                        runCatching {
                            this.navigate(route = route, builder = navOptionsBuilder)
                        }.onFailure {
                            //log this failure for the route
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import kotlinx.serialization.Serializable

                    sealed class Dest{
                        @Serializable
                        data object Home : Dest()

                        @Serializable
                        data class Profile(val dummy: Dummy) : Dest()
                    }

                    @Parcelize
                    @Serializable
                    data class Dummy(
                        val name: String,
                        val age: Int
                    ) : Parcelable

                    class CustomNavType<T : Parcelable>(
                        private val clazz: KClass<T>,
                        private val serializer: KSerializer<T>
                    ) : NavType<T>(false) {
                        override fun get(bundle: Bundle, key: String): T? {
                            return if (Build.VERSION.SDK_INT >= Build.VERSION_CODES.TIRAMISU) {
                                bundle.getParcelable(key, clazz.java)
                            } else {
                                bundle.getParcelable(key)
                            }
                        }

                        override fun parseValue(value: String): T {
                            return Json.decodeFromString(serializer, value)
                        }

                        override fun put(bundle: Bundle, key: String, value: T) {
                            bundle.putParcelable(key, value)
                        }

                        override fun serializeAsValue(value: T): String {
                            return Json.encodeToString(serializer, value)
                        }
                    }

                    @Composable
                    fun MainNavigation(modifier: Modifier = Modifier) {
                        val navController = rememberNavController()
                        NavHost(navController = navController, startDestination = Dest.Home) {
                            composable<Dest.Home> {
                                HomeScreen {
                                    navController.navigate(Dest.Profile(Dummy(name = "Hirva", age = 5)))
                                }
                            }

                            composable<Dest.Profile>(
                                typeMap = mapOf(
                                    typeOf<Dummy>() to CustomNavType(
                                        Dummy::class,
                                        Dummy.serializer()
                                    )
                                )
                            ) {
                                val profile = it.toRoute<Dest.Profile>()
                                ProfileScreen(profile) {
                                }
                            }
                        }
                    }

                    @Composable
                    fun ProfileScreen(profile:Dest.Profile, onClick: () -> Unit) {
                        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                            Text(text = profile.dummy.name, modifier = Modifier.clickable { onClick.invoke() })
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Navigation Koin",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/mahdizareeii/Mini-Uber.git
            KotlinCode(
                code = """
                    class NavigationRouter : Router {
                        private var navController: NavController? = null

                        fun initNavController(navController: NavController) {
                            this.navController = navController
                        }

                        override fun navigate(route: String) {
                            navController?.navigate(route)
                        }

                        override fun navigateBack() {
                            navController?.navigateUp()
                        }
                    }

                    interface Router {
                        fun navigate(route: String)
                        fun navigateBack()
                    }
                    
                    val navigationModule = module {
                        single { NavigationRouter() } bind Router::class
                    }
                    
                    fun initKoin(koinContext: (KoinApplication) -> Unit) {
                        startKoin {
                            koinContext.invoke(this)
                            modules(getKoinModules())
                        }
                    }

                    fun getKoinModules() = listOf(
                        navigationModule
                    )
                    
                    MaterialTheme {
                        val navController = rememberNavController()
                        
                        //init App router
                        getKoin().get<NavigationRouter>().initNavController(navController)
                        
                        NavHost(
                            navController = navController,
                            startDestination = AppRoute.Home.route
                        ) { }
                    }
                    """.trimIndent()
            )

            //https://github.com/xephosbot/AnilibriaRefresh.git
            KotlinCode(
                code = """
                    //typealias navigation
                    
                    typealias NavEntryBuilder = NavGraphBuilder.(Navigator<*>) -> Unit
                    """.trimIndent()
            )

            SpanText(
                text = "Scroll Blur BottomBar Navigation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            //https://github.com/ahmadsufyan455/subscription-manager.git
            KotlinCode(
                code = """
                    @OptIn(ExperimentalMaterial3Api::class, ExperimentalHazeMaterialsApi::class)
                    @Composable
                    fun MainScreen(
                        navController: NavHostController = rememberNavController()
                    ) {
                        val hazeState = rememberHazeState()

                        val bottomSheetState = rememberModalBottomSheetState(
                            skipPartiallyExpanded = true
                        )

                        Scaffold(
                            containerColor = MaterialTheme.colorScheme.surface,
                            bottomBar = {
                                BottomNavigationBar(
                                    navController = navController,
                                    onAddSubscriptionClick = { },
                                    modifier = Modifier.hazeEffect(hazeState, style = HazeMaterials.ultraThin())
                                )
                            }
                        ) { contentPadding ->
                            NavHost(
                                navController = navController,
                                startDestination = BottomNavItem.Home.route,
                                modifier = Modifier
                                    .hazeSource(state = hazeState)
                            ) {
                                composable(BottomNavItem.Home.route) { HomeScreen(contentPadding = contentPadding) }
                                composable(BottomNavItem.Settings.route) { SettingsScreen() }
                            }
                        }
                    }

                    @Composable
                    fun BottomNavigationBar(
                        navController: NavHostController,
                        onAddSubscriptionClick: () -> Unit,
                        modifier: Modifier = Modifier
                    ) {
                        val navBackStackEntry by navController.currentBackStackEntryAsState()
                        val currentDestination = navBackStackEntry?.destination

                        NavigationBar(
                            containerColor = Color.Transparent,
                            contentColor = MaterialTheme.colorScheme.onSurface,
                            modifier = modifier
                        ) {
                            bottomNavItems.forEach { item ->
                                val selected = currentDestination?.hierarchy?.any { it.route == item.route } == true

                                // Animate scale for selected items
                                val scale by animateFloatAsState(
                                    targetValue = if (selected && item != BottomNavItem.AddSubscription) 1.1f else 1f,
                                    animationSpec = tween(300),
                                    label = "scale"
                                )

                                // Animate icon color
                                val iconColor by animateColorAsState(
                                    targetValue = when {
                                        selected -> MaterialTheme.colorScheme.primary
                                        else -> MaterialTheme.colorScheme.onSurface
                                    },
                                    animationSpec = tween(300),
                                    label = "iconColor"
                                )

                                NavigationBarItem(
                                    icon = {
                                        if (item == BottomNavItem.AddSubscription) {
                                            Box(
                                                modifier = Modifier
                                                    .clip(CircleShape)
                                                    .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.8f))
                                                    .padding(16.dp)
                                            ) {
                                                Icon(
                                                    painter = item.icon(),
                                                    contentDescription = item.title,
                                                    tint = MaterialTheme.colorScheme.onPrimary
                                                )
                                            }
                                        } else {
                                            Icon(
                                                painter = item.icon(),
                                                contentDescription = item.title,
                                                tint = iconColor,
                                                modifier = Modifier.scale(scale)
                                            )
                                        }
                                    },
                                    selected = selected,
                                    onClick = {
                                        if (item == BottomNavItem.AddSubscription) {
                                            onAddSubscriptionClick()
                                        } else {
                                            navController.navigate(item.route) {
                                                popUpTo(navController.graph.findStartDestination().id) {
                                                    saveState = true
                                                }
                                                launchSingleTop = true
                                                restoreState = true
                                            }
                                        }
                                    },
                                    colors = NavigationBarItemDefaults.colors(
                                        indicatorColor = Color.Transparent
                                    )
                                )
                            }
                        }
                    }

                    @Preview
                    @Composable
                    private fun BottomNavBarPrev() {
                        BottomNavigationBar(
                            navController = rememberNavController(),
                            onAddSubscriptionClick = {}
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Navigation 3",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Full back stack control - Navigate by adding/removing items from a list.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Adaptive layouts - Multiple destinations displayed simultaneously. sceneTransition = { backStack → }",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "State retention - Automatically scoped to back stack entries.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Built-in animations - Smooth transitions between destinations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Metadata support - Content can communicate with parent layouts.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Setup & Dependencies",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "navigation3-runtime - Core API with NavEntry & EntryProvider.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "navigation3-ui - UI components like NavDisplay & Scene.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "lifecycle-viewmodel-navigation3 - ViewModel scoping",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "adaptive-navigation3 - Material 3 adaptive layouts",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Core Components",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ol {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Navigation Keys - Identify your destinations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Navigation Keys - Identify your destinations.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Back stack - The main focus is on the back stack. It represents your app's navigation state. Now we have full control of it using rememberNavBackStack! So instead of NavController, we work directly with the back stack by pushing new keys into it.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "NavDisplay -  NavKey, you can make your backstack persistent in case of configurations changes like rotating the screen. In the old library, we had a NavHost that was the container holding our navigation graph. Instead of this, Navigation uses a NavDisplay that observes your back stack and updates its Ul accordingly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "NavEntry - These are the different destinations that you have in the app. Same as the composable<> we had before in the old Navigation library. You can use the entryProvider DSL to construct it easily. NavEntry Contains:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )

                    Ul {
                        Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Key reference",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Composable content",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }

                        Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                            SpanText(
                                text = "Optional metadata",
                                modifier = Modifier
                                    .fontSize(FontSize.Medium)
                            )
                        }
                    }
                }
            }

            SpanText(
                text = "Creating Navigation Keys",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Navigation keys identify your destinations and should be:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Serializable data classes or objects.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Implement NavKey interface (for state saving).",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Marked with @Serializable annotation.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                    @Serializable
                    data object Product : NavKey

                    @Serializable
                    data class ProductDetail(val productId: Int) : NavKey
                    """.trimIndent()
            )

            SpanText(
                text = "Entry Provider",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Entry Provider converts keys to NavEntry objects. EntryProviders to map each key to its corresponding composable UI.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Two Approaches:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Direct Lambda - Manual when/switch statements",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "entryProvider DSL - Cleaner, type-safe approach",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "NavEntry Contains:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ol {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Key reference",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Composable content",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Optional metadata for customization",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            SpanText(
                text = "Scenes",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The mechanism that lets NavDisplay render more than one destination at once (list-detail, panes, dialogs, etc.).",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "Connecting the Child Entry",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In the ChildScreen entry, we use the metadata parameter to link it to the parent. By providing the parent’s identifier, we tell the system to look for the existing ViewModelStore rather than creating a new one.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "viewModelStoreOwner to LocalSharedViewModelStoreOwner.current, the child screen effectively “borrows” the lifecycle and store of the parent screen. This enables seamless state sharing without the boilerplate code often required by more complex dependency injection setups.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    entry<ChildScreen>(
                        metadata = SharedViewModelStoreNavEntryDecorator.parent(
                            ParentScreen.toString() 
                        )
                    ) {
                        val viewModel = viewModel<CounterViewModel>(
                            viewModelStoreOwner = LocalSharedViewModelStoreOwner.current
                        )
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "NavDisplay Component",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "NavDisplay observes your back stack and renders UI automatically.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Default Behavior: Shows topmost entry in single-pane layout with automatic recomposition on stack changes.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "backStack - Your SnapshotStateList of keys.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "entryProvider - Function to resolve keys to content.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "onBack - Handle back navigation events.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = $$"""
                    //Create a back stack with initial destination
                    val backStack = remember { mutableStateList0f<Any>(Home) }
                  
                    NavDisplay( 
                        backStack = backStack,
                        //Navigate back - remove last key from back stack
                        onBack = { backStack.removeLastOrNull) },   
                        entryProvider = { key -> 
                            when (key) {
                               is Home -> NavEntry(key) {
                                    ContentGreen("Welcome to Nav3") {
                                        Button(onClick = { backStack.add(Product("123")) }) {
                                        Text("Click to navigate")
                                    ｝
                               }
                               
                               is Product -> NavEntry(key) {
                                    ContentBlue("Product ${key.id}")
                               }
                            }
                        }
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "Navigation Animation",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    NavDisplay(
                        transitionSpec = {
                            slideInHorizontally(initialOffsetX = { it }) togetherWith
                            slideOutHorizontally(targetOffsetX = { -it })
                        },
                        popTransitionSpec = {
                            slideInHorizontally(initialOffsetX = { -it }) togetherWith
                                slideOutHorizontally(targetOffsetX = { it })
                        },
                        predictivePopTransitionSpec = {
                            slideInHorizontally(initialOffsetX = { -it }) togetherWith
                                slideOutHorizontally(targetOffsetX = { it })
                        }
                    )
                    """.trimIndent()
            )

            SpanText(
                text = "NavigationRoot (common route create)",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    /**
                     * Base type for all navigation routes in the app.
                     * Extends [NavKey] so that routes work with Navigation3 backstack and [NavDisplay].
                     *
                     * App-level routes (e.g. in composeApp) should implement this interface and be
                     * [kotlinx.serialization.Serializable] for multiplatform state restoration.
                     */

                    interface Route : NavKey

                    //Decorator.kt
                    
                    /**
                    * Composable that creates a decorator injecting [LocalNavigator] via [CompositionLocalProvider]
                    * for each nav entry. Add the returned decorator to [androidx.navigation3.ui.NavDisplay]'s [entryDecorators].
                    */
                    @Composable
                    fun rememberLocalNavigatorDecorator(navigator: Navigator): NavEntryDecorator<Route> {
                        val decorate: @Composable (entry: NavEntry<Route>) -> Unit = { entry ->
                            CompositionLocalProvider(LocalNavigator provides navigator) {
                                entry.Content()
                            }
                        }
                        return remember(navigator) {
                            NavEntryDecorator<Route>(onPop = {}, decorate = decorate)
                        }
                    }
                    
                    /**
                     * A simple [ViewModelStoreOwner] that holds a [ViewModelStore].
                     * Used to scope ViewModels per navigation entry so they can be cleared on pop.
                     */
                    private class NavEntryViewModelStoreOwner(
                        override val viewModelStore: ViewModelStore
                    ) : ViewModelStoreOwner

                    /**
                     * Composable that creates a [NavEntryDecorator] that:
                     * 1. Provides a [ViewModelStoreOwner] per nav entry via [LocalViewModelStoreOwner]
                     * 2. Clears the [ViewModelStore] when the entry is popped (onPop callback)
                     *
                     * This ensures ViewModels (e.g. from [metroViewModel]) are scoped to each screen
                     * and disposed when navigating back, so a fresh instance is created when returning.
                     */
                    @Composable
                    fun rememberViewModelStoreNavEntryDecorator(): NavEntryDecorator<Route> {
                        val storeMap = remember { mutableMapOf<Any, ViewModelStore>() }

                        val decorate: @Composable (entry: NavEntry<Route>) -> Unit = { entry ->
                            val contentKey = entry.contentKey
                            val store = storeMap.getOrPut(contentKey) { ViewModelStore() }
                            val owner = remember(store) { NavEntryViewModelStoreOwner(store) }

                            CompositionLocalProvider(LocalViewModelStoreOwner provides owner) {
                                entry.Content()
                            }
                        }

                        val onPop: (key: Any) -> Unit = { contentKey ->
                            storeMap.remove(contentKey)?.clear()
                        }

                        return remember(storeMap) {
                            NavEntryDecorator(onPop = onPop, decorate = decorate)
                        }
                    }

                    /**
                     * Provides the [Navigator] for the composition tree below [NavigationRoot].
                     * Use [LocalNavigator].current in screens to navigate via [Navigator.navigateTo], [Navigator.pop], [Navigator.navigateToRoot].
                     */
                    val LocalNavigator = staticCompositionLocalOf<Navigator> {
                        error("LocalNavigator used outside NavigationRoot")
                    }

                    /**
                     * Root composable that owns the navigation backstack and hides [NavKey] behind [Route].
                     *
                     * @param initialRoute First route to show (must be a [Route]).
                     * @param savedStateConfig Configuration with polymorphic serialization for all [Route] subtypes (required for state restoration).
                     * @param onBack Called when a back action occurs. Receives the [NavBackStack] so the app can e.g. call [NavBackStack.removeLastOrNull].
                     * @param entryProvider Maps each [Route] to a [NavEntry] that displays the screen for that route.
                     *   Use [LocalNavBackStack].current inside screen content to get the backstack and navigate.
                     */
                    @Composable
                    fun <R : Route> NavigationRoot(
                        initialRoute: R,
                        savedStateConfig: SavedStateConfiguration,
                        onBack: (NavBackStack<Route>) -> Unit,
                        entryProvider: (Route) -> NavEntry<Route>
                    ) {
                        val backStack = rememberNavBackStack(savedStateConfig, initialRoute) as NavBackStack<Route>
                        val navigator = rememberNavigator(backStack, initialRoute)
                        NavDisplay(
                            backStack = backStack,
                            onBack = { onBack(backStack) },
                            entryProvider = { key -> entryProvider(key) },
                            entryDecorators = listOf(
                                rememberSaveableStateHolderNavEntryDecorator(),
                                rememberViewModelStoreNavEntryDecorator(),
                                rememberLocalNavigatorDecorator(navigator),
                            )
                        )
                    }
                    
                    /**
                     * Gateway for navigation. Use [LocalNavigator].current in composables below [NavigationRoot]
                     * instead of mutating the backstack directly.
                     */
                    interface Navigator {
                        /** Pushes [route] onto the backstack. */
                        fun navigateTo(route: Route)

                        /** Pops the current route from the backstack. */
                        fun pop()

                        /** Clears the backstack and shows the initial route passed to [NavigationRoot]. */
                        fun navigateToRoot()

                        /** Clears the backstack and shows [route] (e.g. "home" from splash). */
                        fun navigateToRoot(route: Route)
                    }
                    
                    internal class DefaultNavigator(
                        private val backStack: NavBackStack<Route>,
                        private val rootRoute: Route
                    ) : Navigator {
                        override fun navigateTo(route: Route) {
                            backStack.add(route)
                        }

                        override fun pop() {
                            backStack.removeLastOrNull()
                        }

                        override fun navigateToRoot() {
                            backStack.clear()
                            backStack.add(rootRoute)
                        }

                        override fun navigateToRoot(route: Route) {
                            backStack.clear()
                            backStack.add(route)
                        }
                    }

                    /**
                     * Composable that creates a [Navigator] implementation backed by the given [backStack].
                     * Use this inside [NavigationRoot] so the implementation can be created in a composition context.
                     *
                     * @param backStack The backstack from [rememberNavBackStack].
                     * @param rootRoute The route to show when [Navigator.navigateToRoot] is called.
                     */
                    @Composable
                    fun rememberNavigator(
                        backStack: NavBackStack<Route>,
                        rootRoute: Route
                    ): Navigator = remember(backStack, rootRoute) {
                        DefaultNavigator(backStack, rootRoute)
                    }
                    
                    /**
                     * SavedStateConfiguration for multiplatform - registers all [Route] subtypes
                     * under [NavKey] polymorphic scope (required for backstack state restoration).
                     * The framework serializes keys as NavKey, so subclasses must be registered there.
                     */
                    private val navConfig = SavedStateConfiguration {
                        serializersModule = SerializersModule {
                            polymorphic(NavKey::class) {
                                subclass(SplashRoute::class, SplashRoute.serializer())
                                subclass(MarketRoute::class, MarketRoute.serializer())
                                subclass(SearchRoute::class, SearchRoute.serializer())
                                subclass(CryptoDetailRoute::class, CryptoDetailRoute.serializer())
                            }
                        }
                    }

                    @Composable
                    fun AppNavigation(
                        deeplinkCryptoId: String? = null,
                        onDeeplinkHandled: (() -> Unit)? = null
                    ) {
                        NavigationRoot(
                            initialRoute = if (deeplinkCryptoId != null) MarketRoute else SplashRoute,
                            savedStateConfig = navConfig,
                            onBack = { backStack -> backStack.removeLastOrNull() },
                            entryProvider = { route ->
                                when (route) {
                                    is SplashRoute -> NavEntry(route) {
                                        val navigator = LocalNavigator.current
                                        SplashScreen(
                                            onNavigateToHome = { navigator.navigateToRoot(MarketRoute) }
                                        )
                                    }

                                    else -> throw IllegalStateException("Unknown route: ${'$'}route")
                                }
                            }
                        )
                    }
                    
                    /**
                     * Navigation routes for the app using the shared [Route] type from the navigation module.
                     * All routes are @Serializable for multiplatform state restoration.
                     */
                    @Serializable
                    data object SplashRoute : Route

                    @Serializable
                    data object MarketRoute : Route

                    @Serializable
                    data object SearchRoute : Route

                    @Serializable
                    data class CryptoDetailRoute(val cryptoId: String) : Route
                    """.trimIndent()
            )

            SpanText(
                text = "State Management",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "ViewModel Scoping: Use rememberViewModelStoreNavEntryDecorator() to scope ViewModels to specific NavEntries.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Benefits:",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Ul {
                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "ViewModel created when NavEntry added to stack.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Automatically cleared when NavEntry removed.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Perfect for destination-specific state.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Survives configuration changes.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            //https://github.com/music-assistant/mobile-app.git (use bottomSheetStrategys)
            //https://github.com/arifamsar/Subot.git
            //https://github.com/Rohit-554/Nivi_Full_Stack_KMM.git
            KotlinCode(
                code = """
                    androidx-activity = ""
                    androidx-lifecycle = ""
                    
                    [libraries]
                    androidx-activity-compose = { module = "androidx.activity:activity-compose", version.ref = "androidx-activity" }
                    androidx-lifecycle-runtimeCompose = { module = "org.jetbrains.androidx.lifecycle:lifecycle-runtime-compose", version.ref = "androidx-lifecycle" }

                    @Composable
                    fun NavDisplayWithViewModels() {
                        //Create a back stack with initial destination
                        val backStack = rememberNavBackStack(ScreenA)
                        
                        val bottomSheetStrategy = remember { BottomSheetSceneStrategy<NavKey>() }
                        val dialogStrategy = remember { DialogSceneStrategy<NavKey>() }
                        
                        modifier = modifier,
                         
                        transitionSpec = { // Define custom transitions for screen changes
                            fadeIn(tween(300)) togetherWith fadeOut(tween(300))
                        },
    
                        NavDisplay(
                            entryDecorators = listOf(
                                //Registering the Decorator
                                
                                // Add the default decorators for managing scenes and saving state
                                rememberSceneSetupNavEntryDecorator(), 
                                rememberViewModelStoreNavEntryDecorator(), // Required for ViewModel scoping per entry
                              
                                rememberSharedViewModelStoreNavEntryDecorator(), //you must add the SharedViewModelStoreNavEntryDecorator to your NavDisplay to ensure the ViewModelStore lifecycle is correctly tied to your navigation entries.
                               
                                //rememberSaveableStateHolderNavEntryDecorator(), // Required for saving Compose state per entry
                                rememberSaveableStateHolderNavEntryDecorator(rememberSaveableStateHolder())
                            ),
                            
                            sceneStrategy = bottomSheetStrategy.then(dialogStrategy),
                            
                            backStack = backStack,
                            
                            onBack = { backStack.removeLastOrNull() },
                            
                            entryProvider = entryProvider 
                            {
                                entry<ScreenA> {
                                    //ViewModels created here are scoped to this NavEntry
                                    val viewModel: ScreenAViewModel = viewModel()
                                    ScreenAContent(viewModel)
                                ｝   
                            }
                        )
                    }
                    """.trimIndent()
            )

            //https://medium.com/@domen.lanisnik/scope-of-viewmodels-in-compose-navigation-3-fb0de3aa84e5
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Each NavEntry gets its own ViewModelStoreOwner",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "A new ViewModel instance is created when the entry is added to the back stack.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The ViewModel is cleared when the entry is removed.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "init {} runs each time the destination is entered.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

            }

            KotlinCode(
                code = """
                    material3 = "1"
                    navigation = ""
                    navigation-lifecycle = ""
                    
                    [libraries]
                    navigation-runtime = { module = "androidx.navigation3:navigation3-runtime", version.ref = "navigation" }
                    navigation-ui = { module = "androidx.navigation3:navigation3-ui", version.ref = "navigation" }
                    navigation-lifecycle = { module = "androidx.lifecycle:lifecycle-viewmodel-navigation3", version.ref = "navigation-lifecycle" }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //1.  Define your routes (keys)
                    sealed class AppRoute {
                        data object Home : AppRoute()
                        data object ProductList : AppRoute()
                        data class ProductDetail(val productId: String, val productName: String) : AppRoute()
                    }
                    
                    //2. Define your routes (keys)
                    sealed interface Route
                    
                    object Home: Route
                    data class Detail(val itemId: String) : Route
                    
                    @OptIn(ExperimentalMaterial3Api::class)
                    @Composable
                    fun Nav3SampleApp() {
                        val backStack = remember { mutableStateListOf<AppRoute>(AppRoute.Home) }
                        
                        val currentRoute = backStack.lastOrNull() ?: AppRoute.Home
                        
                        Column {
                            TopAppBar(
                                title = {
                                    Text(
                                        text = when (currentRoute) {
                                            is AppRoute.Home -> "Home"
                                            is AppRoute.ProductList -> "Product List"
                                            is AppRoute.ProductDetail -> "Product Detail: ${currentRoute.productName}"
                                        }
                                    )
                                },
                                navigationIcon = {
                                    if (backStack.size > 1) {
                                        IconButton(onClick = {
                                            backStack.removeLastOrNull()
                                        }) {
                                            Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = "Back")
                                        }
                                    }
                                }
                            )

                            NavDisplay(
                                backStack = backStack,
                                onBack = { backStack.removeLastOrNull() },
                                entryProvider = { route ->
                                    when (route) {
                                        is AppRoute.Home -> NavEntry(route) {
                                            HomeScreen(
                                                onNavigateToProducts = {
                                                    backStack.add(AppRoute.ProductList)
                                                }
                                            )
                                        }
                                        is AppRoute.ProductList -> NavEntry(route) {
                                            ProductListScreen(
                                                onProductClick = { productId, productName ->
                                                    backStack.add(AppRoute.ProductDetail(productId, productName))
                                                }
                                            )
                                        }
                                        is AppRoute.ProductDetail -> NavEntry(route) {
                                            ProductDetailScreen(
                                                productId = route.productId,
                                                productName = route.productName
                                            )
                                        }
                                        else -> NavEntry(Unit) {
                                            error("Unknown Route")
                                        }
                                    }
                                }
                            )
                        }
                    }
                    
                    @Composable
                    fun HomeScreen(onNavigateToProducts: () -> Unit) { }
                    
                    @Composable
                    fun ProductDetailScreen(productId: String, productName: String) { }
                    
                    @Composable
                    fun ProductListScreen(onProductClick: (String, String) -> Unit) { }
                    """.trimIndent()
            )

            //https://github.com/Montfel/FIPE.git
            KotlinCode(
                code = """
                    @Serializable
                    internal sealed interface Screen : NavKey {
                        @Serializable
                        data object Home : Screen

                        @Serializable
                        data class Search(val isByFipe: Boolean) : Screen

                        @Serializable
                        data class Form(val formData: FormData) : Screen

                        @Serializable
                        data class VehicleDetails(val searchRequest: SearchRequest) : Screen
                    }
                    
                    @Stable
                    @Serializable
                    data class FormData(
                        val title: String,
                        val items: PersistentList<FormDataItem>,
                        val onItemClick: (item: FormDataItem) -> Unit
                    )
                    
                    @Composable
                    internal fun NavigationGraph() {
                        val backStack = rememberNavBackStack(Screen.Home)

                        NavDisplay(
                            backStack = backStack,
                            onBack = backStack::removeLastOrNull,
                            entryDecorators = listOf(
                                rememberSaveableStateHolderNavEntryDecorator(),
                                rememberViewModelStoreNavEntryDecorator()
                            ),
                            entryProvider = entryProvider {
                                entry<Screen.Home> {
                                    HomeRoute(onNavigateToSearch = { backStack.add(Screen.Search(it)) })
                                }
                                entry<Screen.Search> {
                                    SearchRoute(
                                        isByFipe = it.isByFipe,
                                        onNavigateToForm = { formData ->
                                            backStack.add(Screen.Form(formData = formData))
                                        },
                                        onNavigateToVehicleDetails = { searchRequest ->
                                            backStack.add(Screen.VehicleDetails(searchRequest))
                                        },
                                        onNavigateBack = backStack::removeLastOrNull
                                    )
                                }
                                entry<Screen.Form> {
                                    FormScreen(
                                        formData = it.formData,
                                        onNavigateBack = backStack::removeLastOrNull
                                    )
                                }
                                entry<Screen.VehicleDetails> {
                                    VehicleDetailsRoute(
                                        searchRequest = it.searchRequest,
                                        onNavigateBack = backStack::removeLastOrNull
                                    )
                                }
                            },
                        )
                    }
                    
                    @Composable
                    internal fun HomeRoute(
                        onNavigateToSearch: (isByFipe: Boolean) -> Unit
                    ) {
                        HomeScreen(
                            onEvent = { event ->
                                when (event) {
                                    HomeEvent.OnSearchByFipeClicked -> onNavigateToSearch(true)
                                    HomeEvent.OnSearchByVehicleClicked -> onNavigateToSearch(false)
                                }
                            }
                        )
                    }
                    
                    @Composable
                    internal fun SearchRoute(
                        isByFipe: Boolean,
                        onNavigateToForm: (formData: FormData) -> Unit,
                        onNavigateToVehicleDetails: (searchRequest: SearchRequest) -> Unit,
                        onNavigateBack: () -> Unit,
                        viewModel: SearchViewModel = koinViewModel { parametersOf(isByFipe) },
                    ) {
                    }
                    
                    @Composable
                    internal fun FormScreen(
                        formData: FormData,
                        onNavigateBack: () -> Unit
                    ) { }
                    """.trimIndent()
            )


            KotlinCode(
                code = """
                    @Serializable
                    sealed interface Route : NavKey {
                        @Serializable
                        data object Splash : Route

                        @Serializable
                        data object Onboarding : Route

                        @Serializable
                        data object Login : Route

                        // Main Flow (with Bottom Navigation) - These require login
                        @Serializable
                        data object Main : Route

                        @Serializable
                        data object Home : Route

                        @Serializable
                        data object Schedule : Route

                        @Serializable
                        data object Transaction : Route

                        @Serializable
                        data object Profile : Route

                        @Serializable
                        data object Settings : Route

                        @Serializable
                        data class SettingsDetail(val settingId: String) : Route

                        @Serializable
                        data object ForgetPassword : Route
                    }
                    """.trimIndent()
            )

            //https://github.com/ResulSilay/Sentinel.git
            KotlinCode(
                code = """
                    enum class SentinelTab {
                        Dashboard,
                        About
                    }
                    
                    @Composable
                    internal fun SentinelNavigationBar(
                        modifier: Modifier = Modifier,
                        selectedTab: SentinelTab,
                        onTabSelected: (SentinelTab) -> Unit,
                    ) {
                        val tabs = SentinelTab.entries

                        var selectedTabItem by remember { mutableStateOf(0f) }
                        var selectedTabWidth by remember { mutableStateOf(0f) }

                        val animX by animateFloatAsState(
                            targetValue = selectedTabItem,
                            animationSpec = SentinelSpringSpec
                        )
                        val animWidth by animateFloatAsState(
                            targetValue = selectedTabWidth,
                            animationSpec = SentinelSpringSpec
                        )

                        Box(
                            modifier = modifier
                                .fillMaxWidth()
                                .padding(horizontal = 24.dp)
                                .height(height = 60.dp),
                        ) {
                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .blur(
                                        radius = 24.dp,
                                        edgeTreatment = BlurredEdgeTreatment.Unbounded
                                    )
                                    .background(
                                        color = Color.Black.copy(alpha = 0.9f),
                                        shape = CircleShape
                                    )
                            )

                            Box(
                                modifier = Modifier
                                    .fillMaxSize()
                                    .clip(shape = CircleShape)
                                    .background(
                                        brush = Brush.verticalGradient(
                                            colors = listOf(
                                                Color.White.copy(alpha = 0.08f),
                                                Color.White.copy(alpha = 0.03f)
                                            )
                                        ),
                                        shape = CircleShape
                                    )
                                    .border(
                                        width = 1.dp,
                                        brush = Brush.linearGradient(
                                            colors = listOf(
                                                Color.White.copy(alpha = 0.3f),
                                                Color.White.copy(alpha = 0.03f),
                                                Color.White.copy(alpha = 0.1f)
                                            )
                                        ),
                                        shape = CircleShape
                                    )
                                    .drawWithContent {
                                        val paddingPx = 6.dp.toPx()

                                        drawRoundRect(
                                            brush = Brush.linearGradient(
                                                colors = listOf(
                                                    Color.DarkGray.copy(alpha = 0.20f),
                                                    Color.DarkGray.copy(alpha = 0.05f),
                                                    Color.Transparent
                                                ),
                                                start = Offset(
                                                    x = 32f,
                                                    y = 0f
                                                )
                                            ),
                                            topLeft = Offset(
                                                x = animX + paddingPx,
                                                y = paddingPx
                                            ),
                                            size = Size(
                                                width = animWidth - (paddingPx * 2),
                                                height = size.height - (paddingPx * 2)
                                            ),
                                            cornerRadius = CornerRadius(
                                                x = 100f,
                                                y = 100f
                                            )
                                        )

                                        drawRoundRect(
                                            brush = Brush.horizontalGradient(
                                                colors = listOf(
                                                    Color.White.copy(alpha = 0.09f),
                                                    Color.Transparent,
                                                    Color.Transparent
                                                ),
                                                startX = animX + 4.dp.toPx(),
                                                endX = animX + animWidth - 4.dp.toPx()
                                            ),
                                            topLeft = Offset(
                                                x = animX + paddingPx,
                                                y = paddingPx
                                            ),
                                            size = Size(
                                                width = animWidth - (paddingPx * 2),
                                                height = size.height - (paddingPx * 2)
                                            ),
                                            cornerRadius = CornerRadius(
                                                x = 100f,
                                                y = 100f
                                            ),
                                            style = Stroke(width = 1.2.dp.toPx())
                                        )

                                        drawContent()
                                    }
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxSize(),
                                    horizontalArrangement = Arrangement.SpaceEvenly,
                                    verticalAlignment = Alignment.CenterVertically
                                ) {
                                    tabs.forEach { tab ->
                                        val isSelected = selectedTab == tab

                                        SentinelNavigationBarItem(
                                            modifier = Modifier
                                                .weight(weight = 1f)
                                                .onGloballyPositioned {
                                                    if (isSelected) {
                                                        @Suppress("AssignedValueIsNeverRead")
                                                        selectedTabItem = it.positionInParent().x
                                                        @Suppress("AssignedValueIsNeverRead")
                                                        selectedTabWidth = it.size.width.toFloat()
                                                    }
                                                },
                                            selected = isSelected,
                                            icon = if (tab == SentinelTab.Dashboard) {
                                                vectorResource(resource = Res.drawable.ic_nav_bar_home)
                                            } else {
                                                vectorResource(resource = Res.drawable.ic_nav_bar_about)
                                            },
                                            label = when (tab) {
                                                SentinelTab.Dashboard -> stringResource(resource = Res.string.nav_bar_dashboard_item)
                                                SentinelTab.About -> stringResource(resource = Res.string.nav_bar_about_item)
                                            },
                                            onClick = {
                                                onTabSelected(tab)
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }

                    @Composable
                    private fun SentinelNavigationBarItem(
                        modifier: Modifier = Modifier,
                        selected: Boolean,
                        icon: ImageVector,
                        label: String,
                        onClick: () -> Unit,
                    ) {
                        val labelColor by animateColorAsState(
                            targetValue = if (selected) colors.onPrimary else colors.tabInactive,
                            animationSpec = tween(180),
                            label = "bottom_tab_label_color",
                        )
                        
                        val contentColor by animateColorAsState(
                            targetValue = if (selected) {
                                MaterialTheme.colorScheme.primary
                            } else {
                                Color.White.copy(alpha = 0.5f)
                            },
                            animationSpec = tween(durationMillis = 400)
                        )

                        Column(
                            modifier = modifier
                                .fillMaxHeight()
                                .clickable(
                                    interactionSource = remember { MutableInteractionSource() },
                                    indication = null,
                                    onClick = onClick
                                ),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center
                        ) {
                            Icon(
                                modifier = Modifier
                                    .size(size = 22.dp)
                                    .scale(scale = if (selected) 1.1f else 1f),
                                imageVector = icon,
                                contentDescription = label,
                                tint = contentColor,
                            )

                            Spacer(modifier = Modifier.height(height = 2.dp))

                            Text(
                                text = label,
                                style = MaterialTheme.typography.labelSmall,
                                color = contentColor,
                                fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal
                            )
                        }
                    }

                    private val SentinelSpringSpec = spring<Float>(
                        dampingRatio = 0.6f,
                        stiffness = Spring.StiffnessLow
                    )
                    """.trimIndent()
            )

            //https://github.com/smithjustinn84-netizen/Memory-Match.git
            KotlinCode(
                code = """
                    @Composable
                    fun <T> SegmentedControl(
                        items: List<T>,
                        selectedItem: T,
                        onItemSelected: (T) -> Unit,
                        labelProvider: @Composable (T) -> String,
                        modifier: Modifier = Modifier,
                    ) {
                        val selectedIndex = items.indexOf(selectedItem)
                        val shape = RoundedCornerShape(CORNER_PERCENT)
                        val colors = PokerTheme.colors
                        val spacing = PokerTheme.spacing

                        BoxWithConstraints(
                            modifier =
                                modifier
                                    .height(48.dp)
                                    .clip(shape)
                                    .background(colors.pillUnselected)
                                    .padding(spacing.extraSmall),
                        ) {
                            val itemWidth = maxWidth / items.size

                            val indicatorOffset by animateDpAsState(
                                targetValue = itemWidth * selectedIndex,
                                label = "indicatorOffset",
                            )

                            // Selected Indicator
                            Box(
                                modifier =
                                    Modifier
                                        .width(itemWidth)
                                        .fillMaxHeight()
                                        .offset(x = indicatorOffset)
                                        .clip(shape)
                                        .background(colors.pillSelected),
                            )

                            // Labels
                            Row(modifier = Modifier.fillMaxSize()) {
                                items.forEach { item ->
                                    Box(
                                        modifier =
                                            Modifier
                                                .width(itemWidth)
                                                .fillMaxHeight()
                                                .clickable { onItemSelected(item) },
                                        contentAlignment = Alignment.Center,
                                    ) {
                                        val isSelected = item == selectedItem
                                        Text(
                                            text = labelProvider(item),
                                            style = PokerTheme.typography.labelLarge,
                                            fontWeight = if (isSelected) FontWeight.Bold else FontWeight.Medium,
                                            color = if (isSelected) colors.feltGreenDark else Color.White.copy(alpha = 0.7f),
                                            textAlign = TextAlign.Center,
                                        )
                                    }
                                }
                            }
                        }
                    }

                    private const val CORNER_PERCENT = 50
                    """.trimIndent()
            )

            //https://github.com/stoyan-vuchev/magic-message.git (Bottom Bar Hide/Show Animation, Setting List Good Animation)
            KotlinCode(
                code = """
                    @Immutable
                    object BottomBarTokens {
                        internal val BottomBarHeight: Dp get() = 72.dp
                        internal val BottomBarItemHorizontalPadding: Dp = 8.dp
                    }
                    
                    modifier.defaultMinSize(minHeight = NavigationBarHeight)
                """.trimIndent()
            )
        }
    }
}

//https://www.youtube.com/watch?v=2oqjhkB5UAs (Navigation3 Video)
//https://www.youtube.com/watch?v=lKuPqQxU374&t=1626s (Navigation 3)
//https://medium.com/@shivayogih25/jetpack-compose-deep-linking-type-safe-navigation-done-right-ed79da513c5b (deep link navigation)
//https://github.com/bharat1320/ComposeFluidWaterDropNav
//https://github.com/lluzalves/KoinAndNavigation3.git
//https://github.com/Montfel/FIPE.git (Navigation 3, Platform Specific Navigation)
//https://github.com/logomount/galaxy-navigation.git (navDeepLink navigation / deep link navigation)
//Deep linking (Appsflyer) — Open specific screens from external links and campaigns.
//https://github.com/stoyan-vuchev/magic-message.git (Bottom Bar Hide/Show Animation, Setting List Good Animation)
//https://github.com/music-assistant/mobile-app.git (Navigation3)
//https://github.com/ShanuDevCodes/KITO.git (Best Navigation3 Animation, Upcoming Events logic, Find Dynamic Position, Bottom Hide/Show Animation, hazeEffect, Calendar, Auto Scroll Index)
//https://github.com/arifamsar/Subot.git (Navigation3 (Login root - Main root) multiple root, entryProvider)
//https://github.com/Chouten-App/ChoutenKMP.git (BottomBar Navigation Good with Click Hide show Search Bar Animation)
//https://github.com/Rohit-554/Nivi_Full_Stack_KMM.git (rememberViewModelStoreNavEntryDecorator)
//https://github.com/ResulSilay/Sentinel.git (Bottom Navigation Bar Good)
//https://github.com/Rohit-554/CMP-TODO-LIST (Bottom NavigationBar Good)
//https://medium.com/kotlinkenya-android254/bottom-navigation-with-navigation-3-6087f9ce63b8
//https://github.com/jurajkusnier/fluid-bottom-navigation
//https://github.com/UmairOye/ComposeCurvedBottomNavigationView.git
//https://github.com/ensodai/avalon_navigation
//https://github.com/KuLiPai/JNU-EXAM-Client.git (BottomBar Liquid)
//https://github.com/tbahlai/nav3-transitions
//https://github.com/tbahlai/AdaptiveLayoutNav3.git
//https://github.com/Syedwaheed/CineVault.git (LocalNavAnimatedContentScope, LocalAnimatedVisibilityScope, LocalSharedTransitionScope, BottomNavigation Width Scroll Manage)
//https://github.com/MehdiSekoba/AnimatedBottomBar (Animation)
//https://github.com/darkpandawarrior/Mileway (Good BottomBar)
//https://github.com/Yoke0/Gainful (Calendar, BottomBar Background Spring Animation, Tab Background Spring Animation)
////https://github.com/Hussienfahmy/GPA_Manager.git (LazyColumn Swap Item Animation (MoveUp - MoveDown Item, Firebase Notification (Good), Platform Navigation)
//https://github.com/hyperiontechllc/fluid-bottom-bar (Bottom NavigationBar)
//https://github.com/DimiVlachos/compose-lab (Bottom NavigationBar Animation)

//https://github.com/jurajkusnier/fluid-bottom-navigation

//Check Navigation Deep Link
//adb shell am start -W -a android.intent.action.VIEW -d "app://mykmpapp.com/detail/android-rules"
//xcrun simctl openurl booted "app://mykmpapp.com/detail/ios-rules"